/**
 * Net ⇄ gross for price ENTRY (#2967, docs/plans/net-price-entry.md).
 *
 * Prices are stored and charged gross. When a tenant enters prices net, the
 * admin converts here and nowhere else: the typed net becomes the stored gross
 * on save, and a stored gross is shown back as net.
 *
 * Cents, half-up, the same rounding as the server's VAT breakdown
 * (`new-backend/admin/src/lib/pricing/vatBreakdown.ts` › `splitGroup`:
 * `net = round(gross · 10000 / (10000 + bp))`), so the admin and an invoice
 * never disagree about the net of a stored price.
 */

const toBasisPoints = (ratePercent: number): number => Math.round(ratePercent * 100);
const toCents = (amount: number): number => Math.round(amount * 100);

/** Half-up division of non-negative integers. */
const divideHalfUp = (numerator: number, denominator: number): number =>
    Math.floor((2 * numerator + denominator) / (2 * denominator));

/** The gross a net amount becomes at `ratePercent` (e.g. 19), rounded to cents. */
export const grossFromNet = (net: number, ratePercent: number): number => {
    const bp = toBasisPoints(ratePercent);
    return divideHalfUp(toCents(net) * (10000 + bp), 10000) / 100;
};

/** The net of a stored gross amount at `ratePercent`, rounded to cents. */
export const netFromGross = (gross: number, ratePercent: number): number => {
    const bp = toBasisPoints(ratePercent);
    return divideHalfUp(toCents(gross) * 10000, 10000 + bp) / 100;
};

/** A cent amount as the plain text a number input shows ("119", "10.5", "0"). */
export const amountText = (amount: number): string =>
    Number.isFinite(amount) ? String(Math.round(amount * 100) / 100) : '';
